// JST (+09:00) 固定用ユーティリティ（date-fns-tz 依存不要）
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
const YMD_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/** YYYY-MM-DD -> UTC ISO (その日の UTC 00:00:00Z に統一) */
export function ymdToUtcIso(ymd: string): string {
  if (!ymd || !YMD_REGEX.test(ymd)) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 0, 0, 0)).toISOString();
}

/** UTC ISO -> YYYY-MM-DD (JST表示) 既存の 15:00Z(前日) も +9h 補正で正しい日へ */
export function isoToYmd(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const jst = new Date(d.getTime() + JST_OFFSET_MS);
  const y = jst.getUTCFullYear();
  const m = String(jst.getUTCMonth() + 1).padStart(2, "0");
  const day = String(jst.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** 今日 (JST) */
export function todayYmd(): string {
  return isoToYmd(new Date().toISOString());
}

/** ISO / YYYY-MM-DD -> YYYY-MM-DD(JST) */
export function normalizeToYmd(v: string): string {
  if (!v) return "";
  if (YMD_REGEX.test(v)) return v;
  return isoToYmd(v);
}

/**
 * 送信用正規化: 常に「その暦日(JST)の UTC 00:00Z」へ統一する。
 * サーバーは SQL Server の datetime を Kind=Unspecified で読み戻すため、
 * 返却値には Z が付かない。Z 無しの ISO を new Date().toISOString() すると
 * ブラウザが JST ローカルと誤解釈し 9 時間ズレる（前日の 15:00Z 化）ため、
 * いったん JST 日付へ正規化(isoToYmd)してから UTC 00:00Z(ymdToUtcIso)に戻す。
 * これにより Z の有無に関わらず冪等になる。
 */
export function normalizeOutgoingDate(v?: string): string | undefined {
  if (!v) return undefined;
  if (YMD_REGEX.test(v)) return ymdToUtcIso(v);
  const ymd = isoToYmd(v);
  return ymd ? ymdToUtcIso(ymd) : undefined;
}

/** JST 日付でオーバーラップ判定 */
export function isDateRangeOverlap(
  taskStartIso: string | undefined,
  taskEndIso: string | undefined,
  filterStartYmd: string | undefined,
  filterEndYmd: string | undefined
): boolean {
  const startY = taskStartIso ? isoToYmd(taskStartIso) : "";
  const endY = taskEndIso ? isoToYmd(taskEndIso) : startY;
  if (filterStartYmd && endY && endY < filterStartYmd) return false;
  if (filterEndYmd && startY && startY > filterEndYmd) return false;
  return true;
}

/** ISO を JST 日単位で days 加算（内部的に YMD 化 → 変換） */
export function addDaysIso(iso: string, days: number): string {
  if (!iso) return iso;
  const base = isoToYmd(iso);
  if (!base) return iso;
  return ymdToUtcIso(addDaysYmd(base, days));
}

/** YYYY-MM-DD に days 加算 */
export function addDaysYmd(ymd: string, days: number): string {
  if (!YMD_REGEX.test(ymd)) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days, 0, 0, 0));
  return isoToYmd(dt.toISOString());
}

/** 同一 JST 日付か */
export function sameDayJst(a?: string, b?: string): boolean {
  if (!a || !b) return false;
  return isoToYmd(a) === isoToYmd(b);
}

/**
 * 期限の状態（JST 基準）。
 * - "overdue": 期限日（endDate）が今日より前
 * - "today":   期限日が今日
 * - "none":    上記以外、または期限未設定・完了・非表示
 * ダッシュボードの「期限切れ」と同じく endDate を基準に判定する。
 */
export type DeadlineState = "overdue" | "today" | "none";

/** 期限状態ごとの強調色（ダッシュボードの期限切れ・今日の色と合わせる） */
export const DEADLINE_COLORS: Record<Exclude<DeadlineState, "none">, string> = {
  overdue: "#EF4444",
  today: "#F97316",
};

interface DeadlineTarget {
  endDate?: string | null;
  completed?: boolean;
  status?: string;
  childTasks?: DeadlineTarget[];
}

// 完了・非表示・削除は対応不要のため期限の強調・集計の対象外とする
function isDeadlineExcluded(task: DeadlineTarget): boolean {
  return (
    !!task.completed ||
    task.status === "完了" ||
    task.status === "非表示" ||
    task.status === "Deleted"
  );
}

export function getDeadlineState(
  task: DeadlineTarget,
  today: string = todayYmd()
): DeadlineState {
  if (isDeadlineExcluded(task)) return "none";
  const end = isoToYmd(task.endDate);
  if (!end) return "none";
  if (end < today) return "overdue";
  if (end === today) return "today";
  return "none";
}

/** 期限状態の短いラベル（例: "3日超過" / "今日まで"）。強調不要なら null */
export function getDeadlineLabel(
  task: DeadlineTarget,
  today: string = todayYmd()
): string | null {
  const state = getDeadlineState(task, today);
  if (state === "today") return "今日まで";
  if (state !== "overdue") return null;
  const [ey, em, ed] = isoToYmd(task.endDate).split("-").map(Number);
  const [ty, tm, td] = today.split("-").map(Number);
  const days = Math.round(
    (Date.UTC(ty, tm - 1, td) - Date.UTC(ey, em - 1, ed)) / 86400000
  );
  return `${days}日超過`;
}

/**
 * タスク自身または配下のサブタスクに期限切れがあるか。
 * 親が完了・非表示なら配下のサブタスクも対象外とする。
 */
export function hasOverdueInTree(
  task: DeadlineTarget,
  today: string = todayYmd()
): boolean {
  if (isDeadlineExcluded(task)) return false;
  if (getDeadlineState(task, today) === "overdue") return true;
  return (task.childTasks ?? []).some((c) => hasOverdueInTree(c, today));
}

/**
 * タスク（サブタスク含む）を走査し、期限切れ・今日期限の件数を数える。
 * 完了・非表示のタスクは配下のサブタスクごと集計しない。
 */
export function countDeadlineStates(
  tasks: DeadlineTarget[],
  today: string = todayYmd()
): { overdue: number; today: number } {
  const result = { overdue: 0, today: 0 };
  const walk = (list: DeadlineTarget[]) => {
    for (const t of list) {
      if (isDeadlineExcluded(t)) continue;
      const state = getDeadlineState(t, today);
      if (state === "overdue") result.overdue++;
      else if (state === "today") result.today++;
      if (t.childTasks?.length) walk(t.childTasks);
    }
  };
  walk(tasks);
  return result;
}
